"use no memo";

import { Dispatch, SetStateAction, useCallback } from "react";

import { api } from "@/src/api";

import { t } from "i18next";

import { notificationAtom } from "@/src/state/notificationAtom";

import { Notification } from "@/src/components/organisms/Notifications/types";

import { LoginSource, UserGroup, authModule } from "@/src/auth/auth";
import {
  GetServerTimeResponseBody,
  UserDTO,
  UserRole
} from "@/src/types/types";

import { atom, useAtom, useSetAtom } from "jotai";

// Feature flag to control auto-login functionality
// Set to true to re-enable auto-login in the future
// NOTE: Also ensure disableAutologin is set to false in auth.ts core.init() call
const ENABLE_AUTOLOGIN = false;

// artefact from previous implementation -- kept around as a reference
//
// type LoginError =
//   | "InitialLoginError"
//   | "GenericLoginError"
//   | "UserMissingNameException"
//   | "UserNotVerifiedException";

export type AuthState = {
  token: string | null;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  isLoginPopupOpen: boolean;
  isAdmin: boolean;
  isConsumer: boolean;
  isDealer: boolean;
  isEmailVerified: boolean;
  user: UserDTO | null;
  serverTimeDiffMs: number;
};

let loginAndThenInProgress = false;

// This atom should only be accessed via the useAuth hook.
// Direct access to this atom is discouraged to maintain encapsulation.
export const authAtom = atom<AuthState>({
  token: null,
  isAuthenticating: true,
  isAuthenticated: false,
  isLoginPopupOpen: false,
  isAdmin: false,
  isConsumer: false,
  isDealer: false,
  isEmailVerified: false,
  user: null,
  serverTimeDiffMs: 0
});

const getUser = async () => {
  const user = await api.url("/users/details").get().json();
  return user as UserDTO;
};

const getUserStateUpdate = async (): Promise<Partial<AuthState>> => {
  const user = await getUser();
  return {
    user,
    isEmailVerified: user.emailVerified,
    isAdmin: user.role === UserRole.Admin,
    isConsumer: user.role === UserRole.Consumer,
    isDealer: user.role === UserRole.Dealer
  };
};

const getServerTimeDiffUpdate = async (): Promise<Partial<AuthState>> => {
  const clientTime = new Date().getTime();
  try {
    // Fetch the server time
    const res: GetServerTimeResponseBody = await api
      .url(`/serverTime?clientTimeMs=${clientTime}`)
      .get()
      .json();

    const { serverTimeMs } = res;
    if (serverTimeMs !== undefined) {
      return {
        serverTimeDiffMs: serverTimeMs - new Date().getTime()
      };
    } else {
      throw new Error("serverTimeMs is undefined");
    }
  } catch (error) {
    throw error;
  }
};

const getAuthenticationStateUpdate = (): Partial<AuthState> => {
  // no errors = login success
  // -> set isAuthenticating: false and isAuthenticated: true
  return {
    isAuthenticating: false,
    isAuthenticated: true
  };
};

const getLoginStateUpdate = async (): Promise<Partial<AuthState>> => {
  try {
    // async calls
    const [userStateUpdate, serverTimeDiffUpdate] = await Promise.all([
      await getUserStateUpdate(), // get user details
      await getServerTimeDiffUpdate() // get server time
    ]);
    // sync calls
    const authStateUpdate = getAuthenticationStateUpdate(); // update isAuthenticating, isAuthenticated and error
    // return the combined state updates
    return {
      ...userStateUpdate,
      ...serverTimeDiffUpdate,
      ...authStateUpdate
    };
  } catch (error) {
    throw error;
  }
};

const logoutStateUpdate: Partial<AuthState> = {
  isAuthenticating: false,
  isAuthenticated: false,
  isAdmin: false,
  isConsumer: false,
  isDealer: false,
  isEmailVerified: false,
  user: null,
  serverTimeDiffMs: 0
};

const hasErrorResponse = (
  err: unknown
): err is { response: { errorType: string; data?: unknown } } =>
  typeof err === "object" &&
  err !== null &&
  "response" in err &&
  typeof (err as Record<string, unknown>).response === "object" &&
  (err as Record<string, unknown>).response !== null &&
  "errorType" in
    ((err as Record<string, unknown>).response as Record<string, unknown>) &&
  typeof ((err as Record<string, unknown>).response as Record<string, unknown>)
    .errorType === "string";

const handleLoginErrorImpl = (
  error: unknown,
  isDealer: boolean,
  setNotification: Dispatch<SetStateAction<Notification | null>>,
  logout: () => Promise<void>
) => {
  if (!hasErrorResponse(error)) {
    setNotification({ text: t("notifications.loginFailed"), variant: "error" });
    return;
  }

  switch (error.response.errorType) {
    case "UserNotVerifiedException": {
      setNotification({
        text: t("notifications.userNotVerified"),
        variant: "error",
        isAutoHideDisabled: true
      });
      logout();
      break;
    }
    case "UserMissingNameException": {
      setNotification(
        isDealer
          ? {
              text: t("notifications.userMissingNameExceptionDealer"),
              variant: "error"
            }
          : {
              text: t("notifications.userMissingNameException"),
              linkText: `${t(
                "notifications.userMissingNameExceptionErrorMessage"
              )}`,
              variant: "error"
            }
      );
      logout();
      break;
    }
    default: {
      setNotification({
        text: t("notifications.loginFailed"),
        variant: "error"
      });
      break;
    }
  }
};

export const useAuth = () => {
  const [state, setState] = useAtom(authAtom);
  const setNotification = useSetAtom(notificationAtom);

  const updateAuthState = useCallback((stateUpdate: Partial<AuthState>) => {
    setState((prevState) => ({ ...prevState, ...stateUpdate }));
  }, []);

  const logout = useCallback(async () => {
    try {
      await authModule.sendLogoutEvent();
      await authModule.performLogout();
      updateAuthState(logoutStateUpdate);
      localStorage.removeItem("PREVIOUS_SESSION");
      localStorage.removeItem("AUTH_CONNECTION");
    } catch (e) {
      console.error("Logout failed!", e);
    }
  }, [updateAuthState]);

  const handleLoginError = useCallback(
    (error) => {
      handleLoginErrorImpl(error, state.isDealer, setNotification, logout);
    },
    [state.isDealer, setNotification, logout]
  );

  const attemptLoginOnInitialLoad = useCallback(
    async (userGroup: UserGroup, connection?: string | null) => {
      const previousSession = localStorage.getItem(
        "PREVIOUS_SESSION"
      ) as UserGroup | null;
      try {
        await authModule.initAuth(previousSession ?? userGroup, connection);

        // Check if user was already authenticated before initializing
        // If already authenticated, it's a page refresh, not a new autologin
        const wasAlreadyAuthenticated = await authModule.hasValidIdToken();

        // Check if we're on a consumer route (not dealer or admin)
        const isConsumerRoute =
          !globalThis.location.pathname.startsWith("/dealer") &&
          !globalThis.location.pathname.startsWith("/admin");

        // Attempt silent authentication only for consumer users on consumer routes
        // NOTE: Auto-login is currently disabled via ENABLE_AUTOLOGIN flag
        let isAutologinSuccess = false;
        if (
          ENABLE_AUTOLOGIN &&
          (previousSession ?? userGroup) === "consumer" &&
          isConsumerRoute
        ) {
          const silentToken = await authModule.attemptSilentLogin("consumer");
          if (silentToken) {
            isAutologinSuccess = true;
          } else {
            // No SSO session found, continue with standard authentication flow
            updateAuthState({ isAuthenticating: false });
            // isAutologinSuccess remains false, allowing normal auth flow to proceed
          }
        }

        // No valid session to restore: stop here so we don't issue authenticated
        // API calls without a token (which would otherwise surface as a generic
        // "loginFailed" notification on every refresh).
        if (!wasAlreadyAuthenticated && !isAutologinSuccess) {
          if (previousSession) {
            localStorage.removeItem("PREVIOUS_SESSION");
          }
          updateAuthState({ isAuthenticating: false });
          return;
        }

        const update = await getLoginStateUpdate();
        updateAuthState(update);
        localStorage.setItem("PREVIOUS_SESSION", previousSession ?? userGroup);

        // Show autologin success notification only if it was a new autologin
        // (not a page refresh where user was already authenticated)
        if (
          isAutologinSuccess &&
          !wasAlreadyAuthenticated &&
          update.user?.email
        ) {
          setNotification({
            text: t("notifications.autologinSuccess", {
              email: update.user.email
            }),
            variant: "info"
          });
        }
      } catch (error) {
        localStorage.removeItem("PREVIOUS_SESSION");
        updateAuthState({ isAuthenticating: false });
        handleLoginError(error);
      }
    },
    [updateAuthState, handleLoginError, setNotification]
  );

  const refreshUserInfo = useCallback(async () => {
    const previousSession = localStorage.getItem(
      "PREVIOUS_SESSION"
    ) as UserGroup | null;
    if (!previousSession) {
      return;
    }
    try {
      await authModule.initAuth(previousSession);
      await authModule.refreshUserInfo();
      const update = await getUserStateUpdate();
      updateAuthState(update);
    } catch (error) {
      console.error("Token refresh failed!", error);
    }
  }, [updateAuthState]);

  const login = useCallback(
    async (userGroup: UserGroup, source: LoginSource, connection?: string) => {
      try {
        const token = await authModule.performLogin(
          userGroup,
          source,
          connection
        );
        if (token === undefined) {
          return false;
        }
        updateAuthState({ isAuthenticating: true });
        const update = await getLoginStateUpdate();
        await authModule.sendLoginEvent();
        updateAuthState(update);
        localStorage.setItem("PREVIOUS_SESSION", userGroup);
        return true;
      } catch (error) {
        console.error("Login failed!", error);
        localStorage.removeItem("PREVIOUS_SESSION");
        updateAuthState({ isAuthenticating: false });
        handleLoginError(error);
        return false;
      }
    },
    [updateAuthState, handleLoginError]
  );

  const loginAndThen = useCallback(
    async (
      userGroup: UserGroup,
      source: LoginSource,
      callback: () => Promise<void>
    ) => {
      if (loginAndThenInProgress) {
        return;
      }
      loginAndThenInProgress = true;
      updateAuthState({ isLoginPopupOpen: true });
      try {
        const loginOk = await login(userGroup, source);
        if (loginOk) {
          await callback();
        }
      } finally {
        loginAndThenInProgress = false;
        updateAuthState({ isLoginPopupOpen: false });
      }
    },
    [login, updateAuthState]
  );

  const signup = useCallback(
    async (userGroup: UserGroup, source: LoginSource) => {
      try {
        const token = await authModule.performSignup(userGroup, source);
        if (token === undefined) {
          throw new Error("Token is undefined, login failed (popup closed?)");
        }
        await authModule.sendLoginEvent();
        updateAuthState({ isAuthenticating: true });
        const update = await getLoginStateUpdate();
        updateAuthState(update);
        localStorage.setItem("PREVIOUS_SESSION", userGroup);
      } catch (error) {
        console.error("Login failed!", error);
        localStorage.removeItem("PREVIOUS_SESSION");
        updateAuthState({ isAuthenticating: false });
        handleLoginError(error);
      }
    },
    [updateAuthState, handleLoginError]
  );

  const updateUserState = useCallback(
    (user: UserDTO) => {
      updateAuthState({ user });
    },
    [updateAuthState]
  );

  // caller must handle the case where the user is not authenticated
  const checkAuthorization = useCallback(async () => {
    try {
      // case 1: was authenticated, but token is now invalid, logout
      const hasValidToken = await authModule.hasValidIdToken();
      if (!hasValidToken) {
        return logout();
      }

      // case 2: authenticated, check authorization from backend
      const update = await getUserStateUpdate();
      updateAuthState(update);
    } catch (error) {
      console.error("checkAuthorization error:", error);
    }
  }, [logout, updateAuthState]);

  return {
    login,
    loginAndThen,
    logout,
    signup,
    checkAuthorization,
    attemptLoginOnInitialLoad,
    refreshUserInfo,
    updateUserState,
    isAuthenticating: state.isAuthenticating,
    isLoginPopupOpen: state.isLoginPopupOpen,
    isAuthenticated: state.isAuthenticated && !!state.user,
    isEmailVerified: state.isEmailVerified,
    isAdmin: state.isAdmin,
    isConsumer: state.isConsumer,
    isDealer: state.isDealer,
    user: state.user,
    serverTimeDiffMs: state.serverTimeDiffMs
  };
};
